/**
 * Platform-neutral content hashing. Uses Web Crypto `subtle.digest`, available
 * on browsers, Node 20+, and RN (with a polyfill). Used for:
 *  - entity content hashes (decision #9/#20 conflict detection),
 *  - content-addressed asset keys (decision #18 dedup + missing-only sync).
 */

function getSubtle(): SubtleCrypto {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (!c?.subtle) {
    throw new Error("Web Crypto subtle is unavailable. Provide a crypto polyfill.");
  }
  return c.subtle;
}

function toHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i]!.toString(16).padStart(2, "0");
  }
  return out;
}

const encoder = new TextEncoder();

/** SHA-256 of a UTF-8 string, returned as "sha256-<hex>". */
export async function hashString(input: string): Promise<string> {
  const digest = await getSubtle().digest("SHA-256", encoder.encode(input));
  return "sha256-" + toHex(digest);
}

/**
 * Raw SHA-256 digest of bytes as an ArrayBuffer. Used by PKCE (decision #8) to
 * derive a base64url code_challenge from a code_verifier. Copies into a fresh
 * ArrayBuffer-backed view for TS 5.7+ typed-array / SubtleCrypto compatibility.
 */
export async function sha256Bytes(data: ArrayBuffer | Uint8Array): Promise<ArrayBuffer> {
  const view = data instanceof Uint8Array ? data : new Uint8Array(data);
  const buf = new Uint8Array(view.byteLength);
  buf.set(view);
  return getSubtle().digest("SHA-256", buf);
}

/** SHA-256 of raw bytes, returned as "sha256-<hex>" (asset addressing, #18). */
export async function hashBytes(data: ArrayBuffer | Uint8Array): Promise<string> {
  const view = data instanceof Uint8Array ? data : new Uint8Array(data);
  // Copy into a fresh ArrayBuffer-backed view. TS 5.7+ typed-array generics
  // distinguish ArrayBuffer from SharedArrayBuffer, and SubtleCrypto.digest
  // requires the former; the copy guarantees the correct buffer type.
  const buf = new Uint8Array(view.byteLength);
  buf.set(view);
  const digest = await getSubtle().digest("SHA-256", buf);
  return "sha256-" + toHex(digest);
}

/** SHA-256 of a Blob's contents (asset addressing, #18). */
export async function hashBlob(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  return hashBytes(buf);
}

/**
 * Stable JSON stringify (sorted keys) so the same logical object always hashes
 * identically regardless of key insertion order — required for reliable
 * conflict detection (decision #20).
 */
export function stableStringify(value: unknown): string {
  return JSON.stringify(sortKeys(value));
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(obj).sort()) {
      sorted[key] = sortKeys(obj[key]);
    }
    return sorted;
  }
  return value;
}
